import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { money, relativeTime, formatApiError } from "../lib/api";
import PageHeader, { StatusPill } from "../components/app/PageHeader";
import { Plus, Search, Edit, ArrowLeft, X, Users } from "lucide-react";

export default function Clients() {
  const nav = useNavigate();
  const { id } = useParams();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/clients", { params: { q: q || undefined } });
    setClients(data);
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  if (id) return <ClientDetail id={id} onBack={() => nav("/clients")} />;

  return (
    <div data-testid="clients-page">
      <PageHeader
        eyebrow="Directory"
        title="Clients"
        subtitle="Every company you've quoted. Click any to see full history."
        actions={
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="clients-new-btn" className="btn btn-primary">
            <Plus className="w-4 h-4" /> New Client
          </button>
        }
      />
      <div className="p-10">
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-3 w-4 h-4" style={{ color: "var(--text-faint)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" data-testid="clients-search-input" className="input pl-10" />
        </div>
        <div className="surface overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Contact</th>
                <th>Email</th>
                <th>Phone</th>
                <th className="text-right">Added</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr><td colSpan="5" className="text-center py-20">
                  <div className="flex flex-col items-center gap-3">
                    <Users className="w-8 h-8" style={{ color: "var(--text-faint)" }} />
                    <div className="text-sm" style={{ color: "var(--text-muted)" }}>No clients yet.</div>
                  </div>
                </td></tr>
              )}
              {clients.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => nav(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <td className="font-medium">{c.company_name}</td>
                  <td style={{ color: "var(--text-secondary)" }}>{c.contact_name || "—"}</td>
                  <td className="text-xs font-mono" style={{ color: "var(--text-secondary)" }}>{c.email || ""}</td>
                  <td className="text-xs font-mono" style={{ color: "var(--text-secondary)" }}>{c.phone || ""}</td>
                  <td className="text-right text-xs font-mono" style={{ color: "var(--text-faint)" }}>{relativeTime(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <ClientForm editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
         style={{ background: "rgba(7, 9, 26, 0.75)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div className="surface w-full" style={{ maxWidth: 560, background: "var(--surface-1)" }} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function ClientForm({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || { company_name: "", contact_name: "", email: "", phone: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (editing?.id) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      onSaved();
    } catch (e) { alert(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose}>
      <div className="px-6 py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
        <div>
          <div className="eyebrow-accent mb-1">Directory</div>
          <h2 className="font-display text-xl font-bold">{editing ? "Edit Client" : "New Client"}</h2>
        </div>
        <button onClick={onClose} className="btn btn-ghost btn-icon"><X /></button>
      </div>
      <div className="p-6 space-y-4">
        <Field label="Company Name" required>
          <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="client-company-input" className="input" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Name">
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="client-contact-input" className="input" />
          </Field>
          <Field label="Phone">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone-input" className="input" />
          </Field>
        </div>
        <Field label="Email">
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" className="input" />
        </Field>
        <Field label="Address">
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address-input" className="input" />
        </Field>
        <Field label="Notes">
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="client-notes-input" className="input" />
        </Field>
      </div>
      <div className="px-6 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-subtle)" }}>
        <button onClick={onClose} className="btn btn-ghost">Cancel</button>
        <button onClick={save} disabled={saving || !form.company_name} data-testid="client-save-btn" className="btn btn-primary">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1.5">
        {label}
        {required && <span className="ml-1" style={{ color: "var(--accent)" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function ClientDetail({ id, onBack }) {
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const c = await api.get(`/clients/${id}`);
    setClient(c.data);
    const q = await api.get("/quotes", { params: { client_id: id, limit: 200 } });
    setQuotes(q.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!client) return <div className="p-10 font-mono text-xs" style={{ color: "var(--text-muted)" }}>Loading…</div>;

  return (
    <div data-testid="client-detail-page">
      <PageHeader
        eyebrow="Client"
        title={client.company_name}
        subtitle={client.contact_name || client.email || ""}
        actions={
          <div className="flex gap-2">
            <button onClick={onBack} data-testid="client-back-btn" className="btn btn-secondary">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button onClick={() => setEditing(true)} data-testid="client-edit-btn" className="btn btn-secondary">
              <Edit className="w-3.5 h-3.5" /> Edit
            </button>
            <button onClick={() => nav(`/quotes/new?client=${id}`)} data-testid="client-new-quote-btn" className="btn btn-primary">
              <Plus className="w-4 h-4" /> New Quote
            </button>
          </div>
        }
      />
      <div className="p-10 grid grid-cols-12 gap-6">
        <div className="col-span-4 space-y-4">
          <div className="surface p-6 space-y-4 text-sm">
            <DetailRow label="Contact" value={client.contact_name} />
            <DetailRow label="Email" value={client.email} mono />
            <DetailRow label="Phone" value={client.phone} mono />
            <DetailRow label="Address" value={client.address} />
          </div>
          {client.notes && (
            <div className="surface p-6">
              <div className="label-eyebrow mb-2">Notes</div>
              <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{client.notes}</div>
            </div>
          )}
        </div>
        <div className="col-span-8">
          <div className="surface overflow-hidden">
            <div className="px-6 py-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="label-eyebrow mb-1">History</div>
              <h2 className="font-display text-xl font-bold">Quotes ({quotes.length})</h2>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Quote #</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Updated</th>
                </tr>
              </thead>
              <tbody>
                {quotes.length === 0 && (
                  <tr><td colSpan="5" className="text-center py-16" style={{ color: "var(--text-muted)" }}>No quotes for this client yet.</td></tr>
                )}
                {quotes.map((q) => (
                  <tr key={q.id} className="clickable" onClick={() => nav(`/quotes/${q.id}`)}>
                    <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                      {q.quote_number}{q.is_variation && <span className="ml-2 text-[10px] font-bold text-gradient">VAR</span>}
                    </td>
                    <td className="font-medium">{q.title}</td>
                    <td><StatusPill status={q.status} /></td>
                    <td className="text-right font-mono" data-num>{money(q.total)}</td>
                    <td className="text-right text-xs font-mono" style={{ color: "var(--text-faint)" }}>{relativeTime(q.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editing && <ClientForm editing={client} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""}`} style={{ color: "var(--text-primary)" }}>
        {value || "—"}
      </div>
    </div>
  );
}
